import { useState, useEffect, useCallback } from 'react';
import { Mail, ExternalLink, Check, AlertCircle, Loader2, RefreshCw } from '../../icons/wp-icons';
import { getEmailProviders, emailOAuthApi } from '../../api/emailOAuthApi';

function EmailStep({ data = {}, onDataChange }) {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [connectedProvider, setConnectedProvider] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadProviders = useCallback(async () => {
    try {
      const result = await getEmailProviders();
      setProviders(result.providers || []);

      // Prüfen ob bereits ein Provider verbunden ist
      const connected = result.providers?.find(p => p.isConnected);
      setConnectedProvider(connected || null);
    } catch (err) {
      console.error('Fehler beim Laden der E-Mail-Provider:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadProviders();
    // Immer valid, da optional
    onDataChange({}, true);

    // Auf OAuth-Erfolg vom Popup-Fenster hoeren
    const handleMessage = (event) => {
      if (event.data?.type === 'EMAIL_OAUTH_SUCCESS') {
        console.log('OAuth erfolgreich:', event.data);
        // Status automatisch aktualisieren
        loadProviders();
      }
    };

    // Fallback: Bei Window-Focus Status aktualisieren (falls postMessage nicht funktioniert)
    const handleFocus = () => {
      // Nur aktualisieren wenn noch kein Provider verbunden ist
      if (!connectedProvider) {
        loadProviders();
      }
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('focus', handleFocus);
    };
  }, [connectedProvider]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadProviders();
  };

  const handleConnect = async (provider) => {
    setConnecting(provider.id);
    try {
      // WICHTIG: Popup SOFORT öffnen bevor async-Call!
      // Mobile Browser/PWAs blockieren window.open() wenn es nicht direkt durch User-Aktion kommt
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        'about:blank',
        'emailOAuthPopup',
        `width=${width},height=${height},left=${left},top=${top},popup=yes`
      );

      // Falls Popup blockiert wurde
      if (!popup) {
        alert('Bitte erlauben Sie Popups für diese Seite, um die Verbindung herzustellen.');
        setConnecting(null);
        return;
      }

      // Lade-Nachricht anzeigen
      popup.document.write('<html><body style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;background:#f9fafb;"><p>Laden...</p></body></html>');

      // Auth-URL vom Backend holen - 'both' für Rechnungen UND KI-Agent
      let authData;
      if (provider.id === 'gmail') {
        authData = await emailOAuthApi.gmail.getAuthUrl('both');
      } else if (provider.id === 'outlook') {
        authData = await emailOAuthApi.outlook.getAuthUrl('both');
      }

      if (authData?.authUrl) {
        popup.location.href = authData.authUrl;
      } else {
        popup.close();
      }
    } catch (err) {
      console.error('Fehler beim Starten der OAuth-Verbindung:', err);
      alert('Fehler beim Verbinden: ' + (err.response?.data?.error || err.message));
    } finally {
      setConnecting(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          E-Mail-Konto verbinden
        </h3>
        <p className="text-gray-600 text-sm mb-6">
          Verbinden Sie Ihr E-Mail-Konto, damit Ihr Assistent Kundenanfragen per E-Mail beantworten kann.
        </p>
      </div>

      {connectedProvider ? (
        <div className="bg-green-50 border border-green-200 rounded-xl p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-sm">
              {connectedProvider.icon ? (
                <img src={connectedProvider.icon} alt={connectedProvider.name} className="w-10 h-10" />
              ) : (
                <Mail className="w-8 h-8 text-green-600" />
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h4 className="text-lg font-medium text-green-900">
                  {connectedProvider.name} verbunden
                </h4>
                <Check className="w-5 h-5 text-green-600" />
              </div>
              {connectedProvider.email && (
                <p className="text-green-700 text-sm">
                  {connectedProvider.email}
                </p>
              )}
            </div>
          </div>
          <p className="text-green-700 text-sm">
            Ihr E-Mail-Konto ist verbunden. Ihr Assistent kann jetzt Kundenanfragen per E-Mail beantworten.
          </p>
        </div>
      ) : (
        <>
          {/* E-Mail Provider */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {providers.map((provider) => (
              <button
                key={provider.id}
                onClick={() => handleConnect(provider)}
                disabled={connecting === provider.id}
                className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:border-[#378ab2] hover:bg-[#378ab2]/5 transition text-left disabled:opacity-50 disabled:cursor-wait"
              >
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                  {connecting === provider.id ? (
                    <Loader2 className="w-6 h-6 text-[#378ab2] animate-spin" />
                  ) : provider.icon ? (
                    <img src={provider.icon} alt={provider.name} className="w-8 h-8" />
                  ) : (
                    <Mail className="w-6 h-6 text-gray-500" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{provider.name}</div>
                  <div className="text-sm text-gray-500">
                    {connecting === provider.id ? 'Verbinde...' : `Mit ${provider.name} verbinden`}
                  </div>
                </div>
                <ExternalLink className="w-5 h-5 text-gray-400" />
              </button>
            ))}
          </div>

          {/* IMAP/SMTP Option */}
          <div className="border-t pt-6">
            <div className="bg-gray-50 rounded-xl p-6">
              <h4 className="font-medium text-gray-900 mb-2">
                Andere E-Mail-Anbieter
              </h4>
              <p className="text-gray-600 text-sm mb-4">
                Sie können auch manuell IMAP/SMTP-Zugangsdaten eingeben. Diese Option finden Sie später in den Einstellungen.
              </p>
              <a
                href="/einstellungen?tab=email"
                className="text-[#378ab2] hover:underline text-sm inline-flex items-center gap-1"
              >
                Zu den E-Mail-Einstellungen
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Status aktualisieren */}
          <div className="flex justify-center">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 text-sm text-[#378ab2] hover:bg-[#378ab2]/10 rounded-lg transition disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Wird aktualisiert...' : 'Status aktualisieren'}
            </button>
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <strong>Tipp:</strong> Nach erfolgreicher Verbindung im Popup-Fenster wird der Status automatisch aktualisiert.
              Dieser Schritt ist optional - Sie koennen die E-Mail-Verbindung auch spaeter einrichten.
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default EmailStep;
